/**
 * Wire values are SCREAMING_SNAKE because they are enum members; a person
 * reading a table is not.
 */
export const ORGANIZATION_ROLES = [
  'ORGANIZATION_OWNER',
  'ORGANIZATION_ADMIN',
  'ORGANIZATION_DEVELOPER',
  'ORGANIZATION_ANALYST',
  'ORGANIZATION_READ_ONLY',
] as const;

export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

/**
 * What each role can actually do, in the words of the thing being decided.
 *
 * Shown next to the choice, because "Analyst" tells an admin nothing about
 * whether that person will be able to revoke a license.
 */
export const ROLE_DESCRIPTIONS: Readonly<Record<OrganizationRole, string>> = {
  ORGANIZATION_OWNER: 'Full control, including billing, and can transfer ownership.',
  ORGANIZATION_ADMIN: 'Manages members, products and licenses. Cannot act on an owner.',
  ORGANIZATION_DEVELOPER: 'Integrates: API credentials, environments and webhooks.',
  ORGANIZATION_ANALYST: 'Reads usage and analytics. Changes nothing.',
  ORGANIZATION_READ_ONLY: 'Reads. Changes nothing, anywhere.',
};

export function roleLabel(role: string): string {
  const bare = role
    .replace(/^ORGANIZATION_/, '')
    .replaceAll('_', ' ')
    .toLowerCase();
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}

/**
 * Which roles a given role may grant (§24).
 *
 * The API enforces this; the console mirrors it so the operator is never offered
 * a choice that will be refused. Mirrored deliberately rather than fetched: a
 * dropdown that can only be validated by submitting it is a worse experience
 * than one that shows the truth up front — and the server still decides.
 */
const GRANTABLE: Readonly<Record<string, readonly OrganizationRole[]>> = {
  ORGANIZATION_OWNER: ORGANIZATION_ROLES,
  ORGANIZATION_ADMIN: [
    'ORGANIZATION_ADMIN',
    'ORGANIZATION_DEVELOPER',
    'ORGANIZATION_ANALYST',
    'ORGANIZATION_READ_ONLY',
  ],
};

export function grantableRoles(
  actorRole: string | null,
  isPlatformOperator: boolean,
): readonly OrganizationRole[] {
  if (isPlatformOperator) return ORGANIZATION_ROLES;
  return actorRole === null ? [] : (GRANTABLE[actorRole] ?? []);
}

const RANK: Readonly<Record<string, number>> = {
  ORGANIZATION_OWNER: 5,
  ORGANIZATION_ADMIN: 4,
  ORGANIZATION_DEVELOPER: 3,
  ORGANIZATION_ANALYST: 2,
  ORGANIZATION_READ_ONLY: 1,
};

/** An admin may not act on an owner. Mirrors the API's rank check (§24). */
export function canActOn(
  actorRole: string | null,
  targetRole: string,
  isPlatformOperator: boolean,
): boolean {
  if (isPlatformOperator) return true;
  if (actorRole === null) return false;
  if (actorRole === 'ORGANIZATION_OWNER') return true;
  if (actorRole !== 'ORGANIZATION_ADMIN') return false;
  return (RANK[targetRole] ?? 0) < RANK['ORGANIZATION_OWNER']!;
}
